import { v4 as uuidv4 } from 'uuid';
import { setHovered, setSelected } from './actions';
import forge from 'apis/forge';
import store from 'redux/store';
import { Pick } from 'redux/mode/classes';
import {
	PartialSceneChat,
	PartialDesignSystem,
	PartialFrame,
	PartialScene,
} from 'dragonmouse-shared/interfaces';

export abstract class Command {
	public readonly id = uuidv4();
	public readonly type: string;

	constructor(type: string) {
		this.type = type;
	}

	abstract execute(): Promise<void>;
	abstract undo(): Promise<void>;

	// The classic Command pattern doesn't feature a "redo" operation
	// Classically, redo just triggers another execute
	// our system is different -- we have many empty commands where execute
	// doesn't do anything, and the command is just a vehicle to host
	// the undo/redo operations. For this reason, we define often want to
	// define a distinct redo.
	// However when no redo is defined, we fall back to the classic Command pattern approach
	async redo(): Promise<void> {
		await this.execute();
	}
}

export class DeleteCommand extends Command {
	promiseToDelete: Promise<void> | undefined;

	constructor(private readonly frameIds: string[]) {
		super('DeleteCommand');
	}

	async execute(): Promise<void> {
		store.dispatch(setSelected(null));
		store.dispatch(setHovered(null));
		this.promiseToDelete = forge.deleteFrames(this.frameIds, true);
		await this.promiseToDelete;
	}

	async undo(): Promise<void> {
		if (this.promiseToDelete) await this.promiseToDelete;

		await Promise.all(
			this.frameIds.map((id) =>
				forge.updateFrame({
					update: {
						id,
						deletedAt: null,
					},
				})
			)
		);
		const selections: Pick[] = this.frameIds.map((id) => new Pick(id));
		store.dispatch(setSelected(selections));
	}
}

export class UpdateFrameCommand extends Command {
	constructor(private readonly before: PartialFrame, private readonly after: PartialFrame) {
		super('UpdateFrameCommand');
	}

	async execute(): Promise<void> {
		await forge.updateFrame({ update: this.after }, true);
	}

	async undo(): Promise<void> {
		await forge.updateFrame({ update: this.before });
	}
}

export class UpdateChatCommand extends Command {
	constructor(private readonly before: PartialSceneChat, private readonly after: PartialSceneChat) {
		super('UpdateChatCommand');
	}

	async execute(): Promise<void> {
		await forge.updateSceneChat({ update: this.after }, true);
	}

	async undo(): Promise<void> {
		await forge.updateSceneChat({ update: this.before });
	}
}

export class UpdateSceneCommand extends Command {
	constructor(private readonly before: PartialScene, private readonly after: PartialScene) {
		super('UpdateSceneCommand');
	}

	async execute(): Promise<void> {
		await forge.updateScene({ update: this.after }, true);
	}

	async undo(): Promise<void> {
		await forge.updateScene({ update: this.before });
	}
}

export class UpdateDesignSystemCommand extends Command {
	constructor(
		private readonly before: PartialDesignSystem,
		private readonly after: PartialDesignSystem
	) {
		super('UpdateDesignSystemCommand');
	}

	async execute(): Promise<void> {
		await forge.updateDesignSystem({ update: this.after }, true);
	}

	async undo(): Promise<void> {
		await forge.updateDesignSystem({ update: this.before });
	}
}

// this command puts a frame update on the undo/redo stack but does not perform
// the update when it's executed.  (this is for situations where executing the
// original operation is complex. we let some other code handle the complexity,
// and pass the frame before/after state into this command to support undo/redo)
export class MemorializeFrameUpdateCommand extends Command {
	constructor(private readonly before: PartialFrame[], private readonly after: PartialFrame[]) {
		super('MemorializeFrameUpdateCommand');
	}

	async execute(): Promise<void> {}

	async undo(): Promise<void> {
		await Promise.all(
			this.before.map((update) =>
				forge.updateFrame({
					update: {
						...update,
					},
				})
			)
		);
	}

	async redo(): Promise<void> {
		await Promise.all(
			this.after.map((update) =>
				forge.updateFrame({
					update: {
						...update,
					},
				})
			)
		);
	}

	isEditOfFrame(frameId: string) {
		if (
			this.before.length === 1 &&
			this.before[0].id === frameId &&
			Object.keys(this.before[0]).includes('siteId') &&
			this.after.length === 1 &&
			this.after[0].id === frameId &&
			Object.keys(this.after[0]).includes('siteId')
		) {
			return true;
		}
		return false;
	}
}

export class MemorializeFrameCreationCommand extends Command {
	constructor(private readonly frameIds: string[]) {
		super('MemorializeFrameCreationCommand');
	}

	async execute(): Promise<void> {}

	async undo(): Promise<void> {
		await forge.deleteFrames(this.frameIds);
	}

	async redo(): Promise<void> {
		this.frameIds.map((frameId) =>
			forge.updateFrame({
				update: {
					id: frameId,
					deletedAt: null,
				},
			})
		);
	}
}

interface SerializedCommand {
	type: string;
	id: string;
	[key: string]: any;
}

// When commands are stored (e.g. in local storage) and retrieved they
// can lose their class structure. So we have ability to deserialize properly.
export function deserializeCommand(command: Command | SerializedCommand): Command {
	if (command instanceof Command) {
		return command;
	}

	switch (command.type) {
		case 'DeleteCommand':
			return new DeleteCommand((command as SerializedCommand).frameIds);
		case 'MemorializeFrameUpdateCommand':
			return new MemorializeFrameUpdateCommand(
				(command as SerializedCommand).before,
				(command as SerializedCommand).after
			);
		case 'MemorializeFrameCreationCommand':
			return new MemorializeFrameCreationCommand((command as SerializedCommand).frameIds);
		case 'UpdateFrameCommand':
			return new UpdateFrameCommand(
				(command as SerializedCommand).before,
				(command as SerializedCommand).after
			);
		case 'UpdateChatCommand':
			return new UpdateChatCommand(
				(command as SerializedCommand).before,
				(command as SerializedCommand).after
			);
		case 'UpdateSceneCommand':
			return new UpdateSceneCommand(
				(command as SerializedCommand).before,
				(command as SerializedCommand).after
			);
		case 'UpdateDesignSystemCommand':
			return new UpdateDesignSystemCommand(
				(command as SerializedCommand).before,
				(command as SerializedCommand).after
			);
		default:
			throw new Error(`Unknown command type: ${command.type}`);
	}
}
